/*
Government Purpose Rights (“GPR”)
Contract No.  W911NF-14-D-0005
Contractor Name:   University of Southern California
Contractor Address:  3720 S. Flower Street, 3rd Floor, Los Angeles, CA 90089-0001
Expiration Date:  Restrictions do not expire, GPR is perpetual
Restrictions Notice/Marking: The Government's rights to use, modify, reproduce, release, perform, display, or disclose this software are restricted by paragraph (b)(2) of the Rights in Noncommercial Computer Software and Noncommercial Computer Software Documentation clause contained in the above identified contract.  No restrictions apply after the expiration date shown above. Any reproduction of the software or portions thereof marked with this legend must also reproduce the markings. (see: DFARS 252.227-7014(f)(2)) 
No Commercial Use: This software shall be used for government purposes only and shall not, without the express written permission of the party whose name appears in the restrictive legend, be used, modified, reproduced, released, performed, or displayed for any commercial purpose or disclosed to a person other than subcontractors, suppliers, or prospective subcontractors or suppliers, who require the software to submit offers for, or perform, government contracts.  Prior to disclosing the software, the Contractor shall require the persons to whom disclosure will be made to complete and sign the non-disclosure agreement at 227.7103-7.  (see DFARS 252.227-7025(b)(2))
*/
import React from "react";
import { navigate } from "gatsby";
import {
  Checkbox,
  Collapse,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add,
  ArrowBack,
  ArrowForward,
  Check,
  Delete,
  EditNote,
  Quiz,
  Save,
} from "@mui/icons-material";
import {
  Goal,
  Lesson,
  LessonType,
  Topic,
  UserTest,
} from "@ictlearningsciences/pal3-core/types";
import {
  copyAndRemove,
  copyAndSet,
  equals,
} from "@ictlearningsciences/pal3-core/helpers";

import { ContainedButton, MyIconButton, OutlinedButton } from "./buttons";
import { Popup } from "./dialog";
import { LoadStatus } from "../helpers";
import {
  getUserTestQuestions,
  useWithUserTest,
} from "../hooks/useWithUserTest";
import { useWithUserStudy } from "../hooks/useWithUserStudy";
import { useWithDialogue } from "../hooks/useWithDialogue";
import { useAppSelector } from "../store/hooks";
import { TooltipOverlay } from "./tooltip";

export function EditUserReflection(props: {
  lesson: Lesson;
  small?: boolean;
}): JSX.Element {
  const { lesson } = props;
  const { userTestSaveState } = useAppSelector((state) => state.userData);
  const {
    reflection,
    refValidation,
    refNeeded,
    getReflection,
    checkReflection,
    editReflection,
    saveReflection,
  } = useWithUserTest(props.lesson);
  const [showReflection, setShowReflection] = React.useState<boolean>(false);
  const [isSaving, setIsSaving] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (showReflection === false) {
      checkReflection();
    }
  }, [showReflection]);

  React.useEffect(() => {
    if (!isSaving) return;
    if (userTestSaveState?.status === LoadStatus.DONE) {
      setIsSaving(false);
      setShowReflection(false);
    } else if (userTestSaveState?.status === LoadStatus.FAILED) {
      setIsSaving(false);
    }
  }, [isSaving, userTestSaveState]);

  function hasUnsavedChanges(): boolean {
    if (reflection?.id) {
      return !equals(reflection, getReflection(props.lesson));
    } else {
      return Boolean(reflection?.notes);
    }
  }

  return (
    <div>
      {props.small ? (
        <MyIconButton
          aria="edit notes button"
          tooltip="Edit notes"
          style={{
            color: getReflection(props.lesson).notes ? "orange" : "#65afe9",
          }}
          onClick={() => setShowReflection(true)}
        >
          <EditNote />
        </MyIconButton>
      ) : (
        <OutlinedButton
          aria="edit notes button"
          icon={<EditNote />}
          endIcon={reflection?.notes ? <Check color="success" /> : undefined}
          onClick={() => setShowReflection(true)}
          style={{ width: 150 }}
        >
          Take Notes
        </OutlinedButton>
      )}
      <Popup
        title={lesson.name}
        fullWidth
        open={showReflection}
        onClose={() => setShowReflection(false)}
        onConfirm={() => {
          setIsSaving(true);
          saveReflection();
        }}
        cancelText="Discard"
        buttonText={
          userTestSaveState?.status === LoadStatus.IN_PROGRESS
            ? "Saving..."
            : "Save"
        }
        buttonDisabled={
          !refValidation?.valid ||
          userTestSaveState?.status === LoadStatus.IN_PROGRESS ||
          (reflection?.notes !== undefined && reflection.notes.length > 1000) ||
          !hasUnsavedChanges()
        }
      >
        <Typography>Take Notes</Typography>
        <TextField
          value={reflection?.notes || ""}
          placeholder="Enter your thoughts and notes for this lesson"
          onChange={(e) => editReflection(e.target.value)}
          style={{ width: "100%" }}
          disabled={userTestSaveState?.status === LoadStatus.IN_PROGRESS}
          error={
            refValidation?.valid === false ||
            Boolean(refNeeded) ||
            userTestSaveState?.status === LoadStatus.FAILED ||
            (reflection?.notes !== undefined && reflection.notes.length > 1000)
          }
          helperText={
            refValidation?.message.text ||
            refNeeded?.text ||
            userTestSaveState.error ||
            (reflection?.notes !== undefined && reflection.notes.length > 1000
              ? "Too many characters"
              : "")
          }
          multiline
          rows={4}
        />
        <Typography
          fontSize={12}
          style={{ display: hasUnsavedChanges() ? "flex" : "none" }}
        >
          You have unsaved changes
        </Typography>
      </Popup>
    </div>
  );
}

export function EditUserTest(props: {
  lesson: Lesson;
  small?: boolean;
}): JSX.Element {
  const { lesson } = props;
  const { userTests, userTestSaveState } = useAppSelector(
    (state) => state.userData
  );
  const { saveTests, deleteTest } = useWithUserTest(props.lesson);
  const { playMessage } = useWithDialogue();

  const [cur, setCur] = React.useState<number>(0);
  const [tests, setTests] = React.useState<UserTest[]>([]);
  const [showTest, setShowTest] = React.useState<boolean>(false);
  const [hasEdits, setHasEdits] = React.useState<boolean>(false);

  const curTest = tests[cur];

  React.useEffect(() => {
    const tests = getUserTestQuestions(userTests, { lesson: props.lesson });
    setTests(tests);
    if (tests.length === 0) {
      setCur(0);
    } else if (cur + 1 > tests.length) {
      setCur(tests.length - 1);
    }
  }, [props.lesson, userTests, showTest]);

  React.useEffect(() => {
    if (userTestSaveState.error) {
      playMessage({
        text: "Oops, something went wrong while saving. Please try again.",
      });
    }
  }, [userTestSaveState]);

  React.useEffect(() => {
    for (const editedTest of tests) {
      const savedTest = userTests.find((t) => t.id === editedTest.id);
      if (!savedTest || !equals(savedTest, editedTest)) {
        setHasEdits(true);
        return;
      }
    }
    setHasEdits(false);
  }, [userTests, tests]);

  function onCreate(): void {
    setTests([
      ...tests,
      {
        id: "",
        lesson: props.lesson.id,
        useAsTest: false,
        question: "",
        answer: "",
        hints: [],
        user: "",
      },
    ]);
    setCur(tests.length);
  }

  function onDelete(): void {
    if (!curTest) return;
    if (!curTest.id) {
      const t = copyAndRemove(tests, cur);
      setTests(t);
      if (t.length === 0) {
        setCur(0);
      } else if (cur + 1 > t.length) {
        setCur(t.length - 1);
      }
    } else {
      deleteTest(curTest);
    }
  }

  function onSave(): void {
    if (!canSave()) return;
    saveTests(tests);
  }

  function onEdit(val: Partial<UserTest>): void {
    if (!curTest) return;
    setTests(copyAndSet(tests, cur, { ...curTest, ...val }));
  }

  function canSave(): boolean {
    for (const test of tests) {
      if (!test.useAsTest && (!test.answer || !test.question)) {
        return false;
      }
      if (test.answer.length > 1000 || test.question.length > 1000) {
        return false;
      }
      for (const hint of test.hints) {
        if (hint.length > 1000) return false;
      }
    }
    return true;
  }

  return (
    <div>
      {props.small ? (
        <MyIconButton
          aria="edit self tests button"
          tooltip="Edit self test(s)"
          style={{ color: tests.length > 0 ? "orange" : "#65afe9" }}
          onClick={() => setShowTest(true)}
        >
          <Quiz />
          <Typography style={{ fontSize: 12 }}>{tests.length}</Typography>
        </MyIconButton>
      ) : (
        <OutlinedButton
          aria="edit self tests button"
          icon={<Quiz />}
          endIcon={
            <Typography style={{ fontSize: 12 }}>({tests.length})</Typography>
          }
          onClick={() => setShowTest(true)}
          style={{ width: 150 }}
        >
          Self Test
        </OutlinedButton>
      )}
      <Popup
        title={lesson.name}
        fullWidth
        open={showTest}
        cancelText="Discard"
        onClose={() => setShowTest(false)}
      >
        <div className="row" style={{ justifyContent: "space-between" }}>
          <Typography>Self-Tests</Typography>
          <Typography>
            {tests.length > 0 ? ` (${cur + 1}/${tests.length})` : "(0)"}
          </Typography>
        </div>
        {tests.length > 0 && curTest ? (
          <div>
            {props.lesson.type !== LessonType.PASSIVE && (
              <FormControlLabel
                label="Use this lesson as a test"
                control={
                  <Checkbox
                    checked={curTest.useAsTest}
                    onChange={() => onEdit({ useAsTest: !curTest.useAsTest })}
                  />
                }
              />
            )}
            <Collapse in={!curTest.useAsTest}>
              <TooltipOverlay id="score-3">
                <TextField
                  value={curTest.question || ""}
                  placeholder="Question"
                  onChange={(e) => onEdit({ question: e.target.value })}
                  style={{ width: "100%", marginBottom: 5 }}
                  disabled={
                    userTestSaveState?.status === LoadStatus.IN_PROGRESS ||
                    curTest.useAsTest
                  }
                  error={
                    curTest.question !== undefined &&
                    curTest.question.length > 1000
                  }
                  helperText={
                    curTest.question !== undefined &&
                    curTest.question.length > 1000
                      ? "Too many characters"
                      : ""
                  }
                  multiline
                  rows={3}
                />
                <TextField
                  value={curTest.answer || ""}
                  placeholder="Answer"
                  onChange={(e) => onEdit({ answer: e.target.value })}
                  style={{ width: "100%", marginBottom: 5 }}
                  disabled={
                    userTestSaveState?.status === LoadStatus.IN_PROGRESS ||
                    curTest.useAsTest
                  }
                  error={
                    curTest.answer !== undefined && curTest.answer.length > 1000
                  }
                  helperText={
                    curTest.answer !== undefined && curTest.answer.length > 1000
                      ? "Too many characters"
                      : ""
                  }
                  multiline
                  rows={3}
                />
              </TooltipOverlay>
              <div className="row" style={{ justifyContent: "space-between" }}>
                <Typography>Hints</Typography>
                <MyIconButton
                  aria="add hint to test button"
                  disabled={curTest.hints.length === 5}
                  onClick={() => onEdit({ hints: [...curTest.hints, ""] })}
                >
                  <Add />
                </MyIconButton>
              </div>
              {curTest.hints.map((hint, i) => (
                <div key={`hint-${i}`} className="row">
                  <TextField
                    value={hint}
                    placeholder={`Hint #${i + 1}`}
                    onChange={(e) =>
                      onEdit({
                        hints: copyAndSet(curTest.hints, i, e.target.value),
                      })
                    }
                    style={{ width: "100%", marginBottom: 5 }}
                    disabled={
                      userTestSaveState?.status === LoadStatus.IN_PROGRESS ||
                      curTest.useAsTest
                    }
                    error={
                      curTest.hints[i] !== undefined &&
                      curTest.hints[i].length > 1000
                    }
                    helperText={
                      curTest.hints[i] !== undefined &&
                      curTest.hints[i].length > 1000
                        ? "Too many characters"
                        : ""
                    }
                    multiline
                    rows={1}
                  />
                  <MyIconButton
                    aria={`delete hint ${i} button`}
                    onClick={() =>
                      onEdit({ hints: copyAndRemove(curTest.hints, i) })
                    }
                  >
                    <Delete />
                  </MyIconButton>
                </div>
              ))}
            </Collapse>
          </div>
        ) : undefined}
        <Grid
          container
          className="row"
          style={{ marginTop: 10, justifyContent: "center", width: "100%" }}
        >
          <MyIconButton
            aria="previous test button"
            disabled={cur === 0}
            onClick={() => setCur(cur - 1)}
          >
            <ArrowBack />
          </MyIconButton>
          {curTest && (
            <ContainedButton
              aria="save user tests button"
              loading={userTestSaveState?.status === LoadStatus.IN_PROGRESS}
              icon={<Save />}
              onClick={onSave}
              style={{ marginLeft: 5, marginRight: 5 }}
              disabled={!canSave() || !hasEdits}
            >
              Save
            </ContainedButton>
          )}
          {curTest && (
            <OutlinedButton
              aria="delete user test button"
              icon={<Delete />}
              onClick={onDelete}
              style={{ marginLeft: 5, marginRight: 5 }}
              loading={userTestSaveState?.status === LoadStatus.IN_PROGRESS}
              disabled={tests.length === 0}
            >
              Delete
            </OutlinedButton>
          )}
          <TooltipOverlay id="score-2">
            <OutlinedButton
              aria="create new self test question button"
              loading={userTestSaveState?.status === LoadStatus.IN_PROGRESS}
              icon={<Add />}
              onClick={onCreate}
              style={{ marginLeft: 5, marginRight: 5 }}
            >
              Add
            </OutlinedButton>
          </TooltipOverlay>
          <MyIconButton
            aria="next test button"
            disabled={tests.length === 0 || cur + 1 === tests.length}
            onClick={() => setCur(cur + 1)}
          >
            <ArrowForward />
          </MyIconButton>
        </Grid>
        {hasEdits ? (
          <Typography
            style={{
              fontSize: 12,
              width: "100%",
              textAlign: "center",
              color: "#e57373",
            }}
          >
            You have unsaved changes
          </Typography>
        ) : undefined}
      </Popup>
    </div>
  );
}

export function SelfTestButton(props: {
  goal?: Goal;
  topic?: Topic;
  active?: boolean;
}): JSX.Element {
  const { user, userTests } = useAppSelector((state) => state.userData);
  const [testQs, setTestQs] = React.useState<UserTest[]>([]);
  const [showButton, setShowButton] = React.useState<boolean>();
  const { isActiveStudy: isInStudy } = useWithUserStudy();

  React.useEffect(() => {
    if (props.topic) {
      setTestQs(getUserTestQuestions(userTests, { topic: props.topic }));
    }
    if (props.goal) {
      setTestQs(getUserTestQuestions(userTests, { goal: props.goal }));
    }
  }, [props.goal, props.topic, userTests]);

  React.useEffect(() => {
    if (!user) return;
    if (testQs.length >= 5) {
      setShowButton(true);
    } else {
      setShowButton(isInStudy("ASVABPrepL2L"));
    }
  }, [testQs, user?.studies]);

  function onStart(): void {
    if (props.goal) {
      navigate(`/test?goal=${props.goal.id}`);
    } else if (props.topic) {
      navigate(`/test?topic=${props.topic.id}`);
    }
  }

  if (!showButton) return <></>;
  return (
    <MyIconButton
      aria="start self test button"
      tooltip="Start self test"
      disabled={testQs.length < 5}
      style={{ color: testQs.length >= 5 ? "#65afe9" : "" }}
      onClick={onStart}
    >
      <Quiz />
      <Typography style={{ fontSize: 12 }}>{testQs.length}</Typography>
    </MyIconButton>
  );
}
